import data from "../../shared/sharedData";
import {
  getCountySourceFromPwd,
  getStateSelectionOptions,
} from "../../../../global/utils/countyHelper";

export default {
  name: "Place of Employment and Wage Information",
  prefix: "F.b-c",
  sections: [
    {
      name: "Place of Employment",
      fields: [
        {
          code: "F.b.1",
          machine_name: "jobAddr1",
          widget: "text",
          label: "Worksite Address",
          validate: [
            "required",
            {
              minLength: 2,
              message: "Please enter full street address.",
            },
            {
              maxLength: 60,
              message: "This field cannot be more than 60 characters long.",
            },
          ],
          maxLength: 60,
          tooltip:
            "Enter the street address of the worksite location where work will be performed. The worksite address must be a physical location and cannot be a P.O. Box.",
        },
        {
          code: "F.b.2",
          machine_name: "jobAddr2",
          widget: "text",
          label: "Worksite Address <i>(apartment/suite/floor and number)</i>",
          validate: [
            {
              maxLength: 60,
              message: "This field cannot be more than 60 characters long.",
            },
          ],
          maxLength: 60,
          tooltip: `If additional space is needed for the street address, use this line. If no additional space is needed, enter "N/A.”`,
        },
        {
          code: "F.b.3",
          machine_name: "jobCity",
          widget: "text",
          label: "City",
          validate: [
            "required",
            {
              minLength: 2,
              message: "Please enter full City.",
            },
            {
              maxLength: 30,
              message: "This field cannot be more than 30 characters long.",
            },
          ],
          maxLength: 30,
          tooltip: "Enter the city of the worksite location.",
        },
        {
          code: "F.b.4",
          machine_name: "jobState",
          widget: "select",
          label: "State",
          selection: (formApi, counties) =>
            getStateSelectionOptions({
              formApi,
              counties,
              msaFieldName: "jobMsa",
              multiSiteField: "jobMultiplesites",
              multiSiteValue: "Yes",
              linkingFieldName: "pwdLinking",
              defaultStates: data.state,
            }),
          validate: ["required"],
          clearSchema: ["registrationNumber", "isRegistrationNumberLinked"],
          disabled: true,
          disableWhenField: (formValues) => !!formValues.registrationNumber,
          tooltip:
            "Enter the State, District, or Territory of the worksite location.",
        },
        {
          code: "F.b.5",
          machine_name: "jobPostcode",
          widget: "text",
          label: "Postal Code",
          validate: ["required", "postalUSA"],
          maxLength: 10,
          tooltip: "Enter the postal (zip) code of the worksite location.",
        },
        {
          code: "F.b.6",
          machine_name: "jobCounty",
          widget: "countyselect_bls",
          label: "County",
          required: true,
          validate: [
            "required",
            {
              required_county: "jobState",
              message: "",
              needsFieldValues: true,
            },
          ],
          countySource: (formApi, counties) => {
            const pwdCountySource = getCountySourceFromPwd({
              formStateValues: formApi.getState().values,
              linkingFieldName: "pwdLinking",
              msaFieldName: "jobMsa",
              filterByMsa: false,
            });
            return pwdCountySource === null ? counties : pwdCountySource;
          },
          clearSchema: ["registrationNumber", "isRegistrationNumberLinked"],
          tooltip: "Enter the county of the worksite location.",
          stateField: "jobState",
          disabled: true,
          disableWhenField: (formValues) => !!formValues.registrationNumber,
          updateBls: "jobMsa",
          restrictCountyOptions: (formApi) =>
            !!formApi.getState().values.pwdLinking,
        },
        {
          code: "F.b.7",
          machine_name: "jobMsa",
          widget: "text",
          label: "Metropolitan Statistical Area (MSA) Name / OES Area Title",
          toolTipContent:
            "Enter the name of the Metropolitan Statistical Area (MSA) Name/Occupational Employment Statistics (OES) Area Title covering the worksite.",
          validate: ["required"],
          disabled: true,
          coloring: "grey",
        },
      ],
    },
    {
      name: "Wage Information",
      fields: [
        {
          isGroup: {
            fields: [
              {
                code: "",
                label: "Your 9141 Prevailing Wage",
                machine_name: "_9141_f4_prevailing_wage",
                widget: "text",
                disabled: true,
              },
              {
                code: "",
                label: "Your 9141 Prevailing Wage Rate",
                machine_name: "_9141_f5_wage_per",
                widget: "text",
                disabled: true,
              },
              {
                code: "",
                label: "Basic Wage Rate Paid",
                tooltip: `Enter the rate of pay to be paid to worker(s). If the wage offer is expressed as a range, enter the bottom 
                  of the wage range to be paid on the "From:" line and enter the top of the wage range on the "To:" line. 
                  Note: If the job opportunity involves work at multiple worksites, additional worksites and the applicable basic rate(s) 
                  of pay will be entered in Appendix A.`,
                machine_name: "_9141_f6_wage_info",
                widget: "label",
              },
              {
                code: "F.b.8a",
                machine_name: "wageFrom",
                widget: "wage",
                label: "From",
                validate: ["required"],
                maxLength: 4, // accounts for the "$" and decimals
                warnings: [
                  {
                    text: `The Wage rate you've entered appears to be lower than the prevailing wage. Please check that you've 
                    entered the correct values. Submitting your application with values lower than the prevailing wage may 
                    result in delays in processing.`,
                    isVisible: {
                      when: "wageFrom",
                      is: [
                        "_9141_f4_prevailing_wage",
                        "_9141_f5_wage_per",
                        //not updated
                        "wagePer",
                      ],
                      operator: "pwComparison",
                    },
                  },
                ],
              },
              {
                code: "F.b.8a",
                machine_name: "wageTo",
                widget: "wage",
                label: "To",
                validate: [
                  {
                    fieldComparison: "wageTo",
                    message:
                      '"To" wage must be greater than or equal to "From" wage',
                    needsFieldValues: true,
                    extraData: {
                      operator: ">=",
                      valueOf: "wageFrom",
                    },
                  },
                ],
                maxLength: 4, // accounts for the "$" and decimals
              },
            ],
          },
        },
        {
          isGroup: {
            code: "F.b.8b",
            machine_name: "wagePer",
            label: "Per (Choose only one)",
            tooltip:
              "Indicate whether the rate of pay is per hour, week, bi-weekly, month, year, or based on a piece rate, by selecting the corresponding box. Make only one selection.",
            required: true,
            fields: [
              {
                widget: "radio",
                choice: "Hour",
                value: "Hour",
                validate: ["required"],
              },
              {
                widget: "radio",
                choice: "Week",
                value: "Week",
                validate: ["required"],
              },
              {
                widget: "radio",
                choice: "Bi-Weekly",
                value: "Bi-Weekly",
                validate: ["required"],
              },
              {
                widget: "radio",
                choice: "Month",
                value: "Month",
                validate: ["required"],
              },
              {
                widget: "radio",
                choice: "Year",
                value: "Year",
                validate: ["required"],
              },
              {
                widget: "radio",
                choice: "Piece Rate",
                value: "Piece Rate",
                validate: ["required"],
              },
            ],
          },
        },
        // {
        // name: "Overtime Available",
        // fields: [
        {
          isGroup: {
            code: "F.b.8c",
            machine_name: "recIsOtAvailable",
            label:
              "Are overtime hours available for this job opportunity at any work locations for the 9142B and Appendix A?*",
            tooltip: `This entry asks about the availability of overtime hours (e.g. weekly hours worked exceeding 40) for all of the worksites for  
              this application, including the worksite location on the 9142B and, if any, the worksite locations on the 9142B                                                                                           
             Appendix A. Select "Yes" or "No" as to whether overtime hours are available at any worksite location or locations on the 
             9142B or Appendix A to this application.`,
            fields: [
              {
                widget: "radio",
                choice: "Yes",
                value: "YES",
                validate: ["required"],
              },
              {
                widget: "radio",
                choice: "No",
                value: "NO",
                validate: ["required"],
                clearSchema: ["wageOtFrom", "wageOtTo"],
              },
            ],
          },
        },
        // ],
        // },
        {
          isGroup: {
            label: "Wage Rate Range for Overtime Pay",
            tooltip: `Enter the range of overtime rate(s) to be paid to the worker(s) for all worksites on the 9142B and, 
            if any, worksites on the 9142B Appendix A.  On the "From" line, enter the lowest rate to be paid for 
            the overtime hours at any of the worksites.  On the "To" line enter the highest rate to be paid for 
            overtime hours at any of the worksites.  The unit of pay (e.g. per hour, week, bi-weekly, month, year, 
              or piece rate) must be the same as the unit selected in Section F.b. Item 8b.  If overtime hours 
              are available but the employer, in compliance with applicable Federal, State, or local law, will 
              pay the basic rate (i.e. Item F.b.8a) for overtime hours, enter the same rate as Item F.b.8a.`,
            isVisible: {
              when: "recIsOtAvailable",
              is: "YES",
            },
            fields: [
              {
                code: "F.b.8d",
                machine_name: "wageOtFrom",
                widget: "wage",
                label: "From",
                // validate: ["required"],
                maxLength: 4, // accounts for the "$" and decimals
                warnings: [
                  {
                    text: "Overtime is paid at 1.5 times the wage rate offered.",
                    isVisible: {
                      when: "wageOtFrom",
                      is: ["wageFrom"],
                      operator: "overtimeComparison",
                    },
                  },
                ],
              },
              {
                code: "F.b.8d",
                machine_name: "wageOtTo",
                widget: "wage",
                label: "To",
                validate: [
                  {
                    fieldComparison: "wageOtTo",
                    message:
                      '"To" wage must be greater than or equal to "From" wage',
                    needsFieldValues: true,
                    extraData: {
                      operator: ">=",
                      valueOf: "wageOtFrom",
                    },
                  },
                ],
                maxLength: 4, // accounts for the "$" and decimals
              },
            ],
          },
        },
        {
          code: "F.b.9",
          machine_name: "wageAdditional",
          widget: "text",
          label:
            "Additional Conditions About the Wage Rate to be Paid at Any Work Locations",
          validate: [
            {
              isRequired: "wagePer",
              message: "This field is required",
              needsFieldValues: true,
              extraData: {
                valueOf: "Piece Rate",
              },
            },
          ],
          maxLength: 120,
          tooltip: `Briefly describe any conditions about the wage rate to be paid at any work locations on the Form ETA-9142B and Appendix A. <br/><br/>
          For example, if the answer to question 8b. is "Piece Rate," enter the units that govern how the piece rate is paid (e.g., 5/8 bushel, 90 pound bag or box, 10 box bin).  
          Please also describe here any bonuses, fringe benefits, subsidized housing or meals, or any other benefits associated with this job opportunity.  
          In addition, if overtime hours are available, the conditions under which overtime hours will be paid at a premium rate must be explained in this field.  
          An overtime premium rate is an additional amount paid to employees for hours worked exceeding a certain number in a day, week, or pay period. 
          If overtime pay at different worksites is subject to different conditions, describe the conditions that apply to different worksites.  
          If no conditions of overtime are listed, the employer is asserting that it will pay the overtime rate(s) of pay found in Item F.b.8d after 40 hours worked in a workweek.  
          Enter "N/A" if there are no additional conditions about the wage rate to be paid.

          Overtime Rate Disclosure Examples:  

          Employers should use the following examples as a guide to their responses, modifying the examples to fit their overtime circumstances and States, as appropriate, in order to clearly disclose wage information to applicants and employees:  
    
          Example 1:  "An overtime premium will be paid when required by Federal, State, or local law, including at time-and-a-half after 40 hours per week, time-and-a-half after 8 hours per day, and double-time after 12 hours per day."  
    
          Example 2:   "An overtime premium will be paid when required by Federal, State, or local law, including at time-and-a-half after 48 hours per week in Minnesota and time-and-a-half after 40 hours per week in Illinois.  No overtime premium will be paid in Wisconsin and Indiana."  
    
          Important Note:  The named States in the examples are provided for illustrative purposes only.  Employers should modify their responses, as necessary.  
        `,
        },
      ],
    },
    {
      name: "Prevailing Wage Determination Information",
      fields: [
        {
          code: "F.b.10",
          machine_name: "jobPwdNumber1",
          widget: "text",
          label: "1st PWD Number Case Number",
          validate: ["required"],
          disabled: true,
          tooltip:
            "Enter the first 14-digit PWD number assigned by FLAG or the National Prevailing Wage Center for the job opportunity listed on the application. Example: P-400-xxxxx-xxxxxx.",
        },
        {
          code: "F.b.10a",
          machine_name: "jobPwdNumber2",
          widget: "text",
          label: "2nd PWD Number Case Number",
          disabled: true,
          tooltip: `Enter the second assigned PWD number, if applicable. If there is not a second PWD Number, enter "N/A" in this field.`,
        },
        {
          code: "F.b.10b",
          machine_name: "jobPwdNumber3",
          widget: "text",
          label: "3rd PWD Number Case Number",
          disabled: true,
          tooltip: `Enter the third assigned PWD number, if applicable. If there is not a third PWD Number, enter "N/A."`,
        },
      ],
    },
    {
      name: "Additional Worksites",
      fields: [
        {
          isGroup: {
            code: "F.c.1",
            label:
              "Will work be performed at worksite locations other than the one identified above?",
            machine_name: "jobMultiplesites",
            tooltip: `Indicate whether the employer's job opportunity will be performed at worksite locations other than the one identified in Section F.b. by marking "Yes" or "No."`,
            required: true,
            // disabled: true,
            fields: [
              {
                widget: "radio",
                choice: "Yes",
                value: "YES",
                validate: ["required"],
              },
              {
                widget: "radio",
                choice: "No",
                value: "NO",
                validate: ["required"],
                setSchema: [
                  {
                    set: "employmentLocations",
                    to: [],
                  },
                ],
              },
            ],
          },
        },
      ],
    },
  ],
};
